import type {MutableRefObject} from 'react';
import type {ConfirmationOption} from 'shared/components/ConfirmationDialog/ConfirmationDialog';
import type {CabinetMesh} from 'components/customer/RoomPlanner/types';
import {deletePlaceholderWithHistory} from 'components/customer/RoomPlanner/helpers/history/placeholderHistory';
import {findPlaceholderGroupOf} from 'components/customer/RoomPlanner/helpers/placeholder/quantity';

type PlaceholderDeleteRefs = {
    group: CabinetMesh;
    placeholderListRef: MutableRefObject<CabinetMesh[]>;
    cabinetGroupListRef: MutableRefObject<CabinetMesh[]>;
    showDialog?: (options: ConfirmationOption) => void;
    onDeleted?: () => void;
};

/**
 * Same as cabinet delete: one copy vs the whole product when quantity > 1.
 *
 * @param {PlaceholderDeleteRefs} args Selected placeholder and scene refs
 * @return {void}
 */
export const confirmPlaceholderDelete = ({
    group,
    placeholderListRef,
    cabinetGroupListRef,
    showDialog,
    onDeleted,
}: PlaceholderDeleteRefs): void => {
    const members = findPlaceholderGroupOf(placeholderListRef.current, group);

    const remove = (all: boolean) => {
        onDeleted?.();
        deletePlaceholderWithHistory(
            group,
            placeholderListRef,
            cabinetGroupListRef,
            {all}
        );
    };

    if (members.length <= 1 || !showDialog) {
        remove(false);
        return;
    }

    showDialog({
        title: 'Delete Item',
        message:
            'There are multiple items of this product. Would you like to delete one or all of them?',
        hideYesButton: true,
        hideNoButton: true,
        buttons: [
            {
                name: 'Delete One',
                show: true,
                action: () => {
                    remove(false);
                },
            },
            {
                name: 'Delete all',
                show: true,
                variant: 'danger',
                action: () => {
                    remove(true);
                },
            },
            {
                name: 'Cancel',
                show: true,
                variant: 'warning',
            },
        ],
    });
};
